import React, { useState } from 'react';
import { History, Edit2, Trash2, X } from 'lucide-react';
import { formatNumber, parseFormattedNumber, formatNumberInput } from '../../utils';

export const HistoryTab = ({ weeklyData = {}, setWeeklyData, projectSettings = {}, isDark, colors }) => {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const allEntries = [];
  Object.entries(weeklyData?.weeks || {}).forEach(([week, entries]) => {
    entries.forEach(entry => allEntries.push({ ...entry, week }));
  });
  allEntries.sort((a, b) => b.week.localeCompare(a.week));

  const getMetricValue = (entry) => {
    const metricType = entry.metricType || projectSettings[entry.project]?.metricType || 'points';
    switch (metricType) {
      case 'points': return entry.points || 0;
      case 'volume': return entry.volume || 0;
      case 'fees': return entry.fees || 0;
      case 'custom': return entry.customMetric || 0;
      default: return entry.points || 0;
    }
  };

  const getMetricLabel = (entry) => {
    const metricType = entry.metricType || projectSettings[entry.project]?.metricType || 'points';
    const customName = entry.customMetricName || projectSettings[entry.project]?.customMetricName;
    switch (metricType) {
      case 'points': return 'Points';
      case 'volume': return 'Volume';
      case 'fees': return 'Fees';
      case 'custom': return customName || 'Custom';
      default: return 'Points';
    }
  };

  const startEdit = (entry) => {
    const metricType = entry.metricType || projectSettings[entry.project]?.metricType || 'points';
    setEditingId(entry.id);
    setEditForm({ 
      volume: formatNumberInput(entry.volume || 0), 
      cost: formatNumberInput(entry.cost || 0), 
      points: formatNumberInput(entry.points || 0),
      fees: formatNumberInput(entry.fees || 0),
      customMetric: formatNumberInput(entry.customMetric || 0),
      metricType: metricType
    });
  };

  const handleNumberChange = (field, value) => {
    const cleanValue = value.replace(/[^0-9.]/g, '');
    setEditForm({ ...editForm, [field]: cleanValue });
  };

  const handleNumberBlur = (field) => {
    const value = editForm[field];
    if (value) {
      const parsed = parseFormattedNumber(value);
      const formatted = formatNumberInput(parsed);
      setEditForm({ ...editForm, [field]: formatted });
    }
  };

  const saveEdit = (entry) => {
    const updatedWeeks = { ...weeklyData.weeks };
    updatedWeeks[entry.week] = updatedWeeks[entry.week].map(e => 
      e.id === entry.id ? { 
        ...e, 
        volume: parseFormattedNumber(editForm.volume), 
        cost: parseFormattedNumber(editForm.cost), 
        points: parseFormattedNumber(editForm.points),
        fees: parseFormattedNumber(editForm.fees),
        customMetric: parseFormattedNumber(editForm.customMetric)
      } : e
    );
    setWeeklyData({ ...weeklyData, weeks: updatedWeeks });
    setEditingId(null);
  };

  const deleteEntry = (entry) => {
    if (confirm('Delete this entry?')) {
      const updatedWeeks = { ...weeklyData.weeks };
      updatedWeeks[entry.week] = updatedWeeks[entry.week].filter(e => e.id !== entry.id);
      if (updatedWeeks[entry.week].length === 0) delete updatedWeeks[entry.week];
      setWeeklyData({ ...weeklyData, weeks: updatedWeeks });
    }
  };

  const getMetricFieldForEdit = (metricType) => {
    switch (metricType) {
      case 'points': return 'points';
      case 'volume': return 'volume';
      case 'fees': return 'fees';
      case 'custom': return 'customMetric';
      default: return 'points';
    }
  };

  const inputClasses = `bg-transparent border rounded px-2 py-1 text-right text-sm focus:outline-none ${isDark ? 'border-slate-600 text-white' : 'border-gray-300 text-gray-900'}`;

  return (
    <div className="space-y-6">
      <div className={`${colors.bgCard} border ${colors.border} rounded-xl p-6 ${colors.cardShadow}`}>
        <h3 className={`text-2xl font-bold mb-6 flex items-center gap-2 ${colors.textPrimary}`}>
          <History size={28} className="text-purple-400" />
          Activity History
        </h3>

        {allEntries.length === 0 ? (
          <div className={`text-center py-12 ${colors.textSecondary}`}>
            <History size={48} className="mx-auto mb-4 opacity-50" />
            <p>No activity history yet</p>
            <p className="text-sm mt-2">Add your first activity in the "Add" tab</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`text-left text-sm border-b ${colors.border} ${colors.textSecondary}`}>
                  <th className="pb-3 font-medium">Week</th>
                  <th className="pb-3 font-medium">Project</th>
                  <th className="pb-3 font-medium text-right">Volume</th>
                  <th className="pb-3 font-medium text-right">Costs</th>
                  <th className="pb-3 font-medium text-right">Metric</th>
                  <th className="pb-3 font-medium text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {allEntries.map(entry => {
                  const isEditing = editingId === entry.id;
                  const metricValue = getMetricValue(entry);
                  const metricLabel = getMetricLabel(entry);
                  const metricType = entry.metricType || projectSettings[entry.project]?.metricType || 'points';
                  const metricField = getMetricFieldForEdit(metricType);

                  return (
                    <tr key={entry.id} className={`border-b ${isDark ? 'border-slate-700/50 hover:bg-slate-700/20' : 'border-gray-100 hover:bg-gray-50'}`}>
                      <td className={`py-3 ${colors.textSecondary}`}>{entry.week}</td>
                      <td className={`py-3 font-medium ${colors.textPrimary}`}>{entry.project}</td>
                      <td className="py-3 text-right">
                        {isEditing ? (
                          <input 
                            type="text" 
                            value={editForm.volume} 
                            onChange={(e) => handleNumberChange('volume', e.target.value)}
                            onBlur={() => handleNumberBlur('volume')}
                            className={`w-24 ${inputClasses}`} 
                          />
                        ) : (
                          <span className={isDark ? 'text-blue-400' : 'text-blue-600'}>${formatNumber(entry.volume || 0)}</span>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        {isEditing ? (
                          <input 
                            type="text" 
                            value={editForm.cost} 
                            onChange={(e) => handleNumberChange('cost', e.target.value)}
                            onBlur={() => handleNumberBlur('cost')}
                            className={`w-20 ${inputClasses}`} 
                          />
                        ) : (
                          <span className={isDark ? 'text-red-400' : 'text-red-600'}>${formatNumber(entry.cost || 0)}</span>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        {isEditing ? (
                          <input 
                            type="text" 
                            value={editForm[metricField]} 
                            onChange={(e) => handleNumberChange(metricField, e.target.value)}
                            onBlur={() => handleNumberBlur(metricField)}
                            className={`w-20 ${inputClasses}`} 
                          />
                        ) : (
                          <div>
                            <span className={isDark ? 'text-purple-400' : 'text-purple-600'}>{formatNumber(metricValue)}</span>
                            <span className={`text-xs ml-1 ${colors.textMuted}`}>{metricLabel}</span>
                          </div>
                        )}
                      </td>
                      <td className="py-3 text-center">
                        {isEditing ? (
                          <div className="flex justify-center gap-1">
                            <button onClick={() => saveEdit(entry)} className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs">Save</button>
                            <button onClick={() => setEditingId(null)} className="text-red-400 hover:text-red-300 p-1"><X size={16} /></button>
                          </div>
                        ) : (
                          <div className="flex justify-center gap-1">
                            <button onClick={() => startEdit(entry)} className="text-blue-400 hover:text-blue-300 p-1"><Edit2 size={16} /></button>
                            <button onClick={() => deleteEntry(entry)} className="text-red-400 hover:text-red-300 p-1"><Trash2 size={16} /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};